import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { getAttachmentUrl } from "@/features/attachments/services/attachmentService";
import { fetchParameterValue } from "@/features/parameter/services/parameterService";
import { fetchUsers } from "@/features/user/services/userService";
const gradientPalette = [
    "from-purple-500 to-pink-400",
    "from-purple-500 to-pink-400",
    "from-purple-500 to-pink-400",
    "from-purple-500 to-pink-400",
    "from-purple-500 to-pink-400",
];
const OrgChart = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [orgChartData, setOrgChartData] = useState([]);
    const { data: organizationName } = useQuery(["parameter", "ORGANISME"], () => fetchParameterValue("ORGANISME"), {
        staleTime: 5 * 60 * 1000,
    });
    useEffect(() => {
        let isMounted = true;
        const loadOrgChart = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const data = await fetchUsers(undefined, undefined, true);
                if (!isMounted)
                    return;
                const chart = buildOrgChart(data.users ?? []);
                setOrgChartData(chart);
            }
            catch (loadError) {
                if (!isMounted)
                    return;
                const message = loadError instanceof Error
                    ? loadError.message
                    : "Impossible de charger l'organigramme.";
                setError(message);
            }
            finally {
                if (isMounted)
                    setIsLoading(false);
            }
        };
        void loadOrgChart();
        return () => {
            isMounted = false;
        };
    }, []);
    return (_jsxs("div", { className: "space-y-4", children: [isLoading && _jsx("p", { className: "text-sm text-gray-600", children: "Chargement de l\u2019organigramme..." }), error && _jsx("p", { className: "text-sm text-red-600", children: error }), !isLoading && !error && orgChartData.length === 0 && (_jsx("p", { className: "text-sm text-gray-600", children: "Aucun utilisateur n\u2019a \u00E9t\u00E9 trouv\u00E9 dans l\u2019organigramme." })), !isLoading && !error && orgChartData.length > 0 && (_jsx("div", { className: "overflow-auto pb-4", children: _jsx("div", { className: "w-max mx-auto px-4 sm:px-6", children: _jsx(OrgChartTree, { nodes: orgChartData }) }) }))] }));
};
function buildOrgChart(users) {
    const nodes = new Map();
    users.forEach((user) => {
        nodes.set(user.id, {
            id: user.id,
            fullName: `${user.first_name} ${user.last_name}`.trim(),
            roleLabel: user.role?.label,
            managerId: user.manager_id ?? null,
            profilePhotoPath: user.profile_photo?.file_path,
            children: [],
        });
    });
    const roots = [];
    nodes.forEach((node) => {
        if (node.managerId && node.managerId !== node.id) {
            const managerNode = nodes.get(node.managerId);
            if (managerNode) {
                node.managerName = managerNode.fullName;
                managerNode.children.push(node);
                return;
            }
        }
        roots.push(node);
    });
    sortOrgChartNodes(roots);
    return roots;
}
function sortOrgChartNodes(nodes) {
    nodes.sort((a, b) => a.fullName.localeCompare(b.fullName, "fr"));
    nodes.forEach((node) => sortOrgChartNodes(node.children));
}
const OrgChartTree = ({ nodes }) => (_jsx("div", { className: "inline-flex flex-col items-center space-y-12", children: nodes.map((node) => (_jsx(OrgChartBranch, { node: node }, node.id))) }));
const OrgChartBranch = ({ node }) => (_jsxs("div", { className: "flex flex-col items-center", children: [_jsx(OrgChartCard, { node: node }), node.children.length > 0 && (_jsxs("div", { className: "mt-6 w-full flex flex-col items-center", children: [_jsx("div", { className: "h-6 w-px bg-purple-200" }), _jsxs("div", { className: "relative flex flex-nowrap justify-center gap-x-10 gap-y-12 pt-6 w-full", children: [_jsx("div", { className: "absolute top-0 left-0 right-0 h-px bg-purple-200", "aria-hidden": true }), node.children.map((child) => (_jsx(OrgChartSubTree, { node: child }, child.id)))] })] }))] }));
const OrgChartSubTree = ({ node }) => (_jsxs("div", { className: "flex flex-col items-center min-w-[180px] max-w-xs", children: [_jsx("div", { className: "w-px h-6 bg-purple-200 mb-4" }), _jsx(OrgChartCard, { node: node }), node.children.length > 0 && (_jsxs("div", { className: "mt-6 w-full flex flex-col items-center", children: [_jsx("div", { className: "h-6 w-px bg-purple-200" }), _jsxs("div", { className: "relative flex flex-wrap justify-center gap-x-8 gap-y-12 pt-6 w-full", children: [_jsx("div", { className: "absolute top-0 left-0 right-0 h-px bg-purple-200", "aria-hidden": true }), node.children.map((child) => (_jsx(OrgChartSubTree, { node: child }, child.id)))] })] }))] }));
const getGradientClass = (label) => {
    const codeSum = label
        .split("")
        .map((char) => char.charCodeAt(0))
        .reduce((sum, code) => sum + code, 0);
    return gradientPalette[codeSum % gradientPalette.length];
};
const getInitials = (fullName) => {
    const parts = fullName.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0)
        return "?";
    if (parts.length === 1)
        return parts[0].slice(0, 2).toUpperCase();
    const [first, last] = [parts[0], parts[parts.length - 1]];
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
};
const OrgChartCard = ({ node }) => (_jsxs("div", { className: "bg-white shadow-md rounded-xl p-4 border border-purple-100 flex flex-col items-center text-center min-w-[200px]", children: [_jsx(OrgChartAvatar, { node: node }), _jsx("p", { className: "mt-4 text-lg font-semibold text-gray-900", children: node.fullName }), _jsx("p", { className: "text-sm text-gray-600", children: node.roleLabel ?? "Rôle non renseigné" }), node.managerName && (_jsxs("p", { className: "mt-2 text-xs text-gray-500", children: ["Manager : ", node.managerName] }))] }));
const OrgChartAvatar = ({ node }) => {
    const [photoUrl, setPhotoUrl] = useState();
    useEffect(() => {
        if (!node.profilePhotoPath) {
            setPhotoUrl(undefined);
            return;
        }
        setPhotoUrl(getAttachmentUrl(node.profilePhotoPath));
    }, [node.profilePhotoPath]);
    if (photoUrl) {
        return (_jsx("img", { src: photoUrl, alt: `Avatar de ${node.fullName}`, className: "w-20 h-20 rounded-full object-cover border border-purple-100 shadow-md" }));
    }
    return (_jsx("div", { className: `w-20 h-20 rounded-full bg-gradient-to-br ${getGradientClass(node.fullName)} text-white flex items-center justify-center text-2xl font-semibold shadow-md`, "aria-hidden": true, children: getInitials(node.fullName) }));
};
export default OrgChart;
